<template>
  <v-menu
    v-model="isMenuOpen"
    offset-y
    left
    nudge-bottom="22"
    :elevation="$vuetify.theme.dark ? 9 : 8"
    content-class="list-style notification-menu-content"
    :close-on-content-click="false"
    @input="onMenuToggle"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-badge
        :content="unreadCount"
        :value="unreadCount > 0"
        color="error"
        overlap
        offset-x="10"
        offset-y="10"
      >
        <v-btn
          icon
          class="app-bar-icon-button"
          aria-label="Notifications"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>{{ icons.mdiBellOutline }}</v-icon>
        </v-btn>
      </v-badge>
    </template>
    <v-card class="app-bar-notification-content-container">
      <perfect-scrollbar
        class="ps-user-notifications"
        :options="perfectScrollbarOptions"
      >
        <v-list class="py-0">
          <!-- Header -->
          <v-list-item
            class="d-flex"
            link
          >
            <div class="d-flex align-center justify-space-between flex-grow-1">
              <span class="font-weight-semibold">Notifications</span>
              <v-chip
                v-if="unreadCount > 0"
                class="v-chip-light-bg primary--text font-weight-semibold"
                small
              >
                {{ unreadCount }} New
              </v-chip>
            </div>
          </v-list-item>
          <v-divider></v-divider>

          <!-- Loading -->
          <v-list-item v-if="loading">
            <v-list-item-content class="text-center">
              <v-progress-circular
                indeterminate
                size="24"
                color="primary"
              ></v-progress-circular>
            </v-list-item-content>
          </v-list-item>

          <!-- Empty State -->
          <v-list-item v-else-if="notifications.length === 0">
            <v-list-item-content class="text-center py-4">
              <span class="text--secondary text-sm">No notifications yet</span>
            </v-list-item-content>
          </v-list-item>

          <!-- Notifications -->
          <template v-else>
            <template v-for="(notification, index) in notifications">
              <v-list-item
                :key="`${notification._source}:${notification.id}`"
                :class="{ 'unread-notification': !notification.is_read }"
                class="notification-item"
                link
                @click="onNotificationClick(notification)"
              >
                <!-- Avatar -->
                <v-list-item-avatar
                  :class="[{'v-avatar-light-bg primary--text justify-center': !getNotificationAvatar(notification)}]"
                  size="38"
                >
                  <v-img
                    v-if="getNotificationAvatar(notification)"
                    :src="getNotificationAvatar(notification)"
                  ></v-img>
                  <v-icon
                    v-else
                    size="22"
                    :color="getNotificationColor(notification.type)"
                  >
                    {{ getNotificationIcon(notification.type) }}
                  </v-icon>
                </v-list-item-avatar>

                <!-- Content -->
                <v-list-item-content>
                  <v-list-item-title class="text-sm font-weight-semibold">
                    {{ notification.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-sm">
                    {{ notification.message }}
                  </v-list-item-subtitle>

                  <!-- Accept / Decline buttons for connection requests -->
                  <div
                    v-if="isActionable(notification)"
                    class="mt-2 d-flex"
                    style="gap: 8px;"
                  >
                    <v-btn
                      x-small
                      color="success"
                      :loading="actionLoading === notification.id + '-accept'"
                      @click.stop="handleConnectionAction(notification, 'accept')"
                    >
                      Accept
                    </v-btn>
                    <v-btn
                      x-small
                      outlined
                      color="error"
                      :loading="actionLoading === notification.id + '-reject'"
                      @click.stop="handleConnectionAction(notification, 'reject')"
                    >
                      Decline
                    </v-btn>
                  </div>

                  <!-- Acted status -->
                  <div
                    v-else-if="notification._actionResult || (notification.data && notification.data.connectionStatus)"
                    class="mt-1"
                  >
                    <v-chip
                      x-small
                      :color="(notification._actionResult || notification.data.connectionStatus) === 'accepted' ? 'success' : 'default'"
                      :text-color="(notification._actionResult || notification.data.connectionStatus) === 'accepted' ? 'white' : ''"
                    >
                      {{ (notification._actionResult || notification.data.connectionStatus) === 'accepted' ? 'Accepted' : 'Declined' }}
                    </v-chip>
                  </div>
                </v-list-item-content>

                <!-- Time -->
                <v-list-item-action>
                  <span class="text--secondary text-xs">{{ formatTime(notification.created_at) }}</span>
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="'d-' + index"></v-divider>
            </template>
          </template>

          <v-list-item
            v-if="notifications.length > 0"
            key="read-all-btn"
            class="read-all-btn-list-item"
          >
            <v-btn
              block
              color="primary"
              :disabled="unreadCount === 0"
              @click="markAllRead"
            >
              Read All Notifications
            </v-btn>
          </v-list-item>
        </v-list>
      </perfect-scrollbar>
    </v-card>
  </v-menu>
</template>

<script>
import { mdiBellOutline, mdiAccountPlusOutline, mdiAccountCheckOutline, mdiAccountOutline, mdiCheckCircleOutline, mdiCloseCircleOutline } from '@mdi/js'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import axios from 'axios'
import referrals from '@/services/referrals'

export default {
  components: {
    PerfectScrollbar,
  },
  data: () => ({
    isMenuOpen: false,
    notifications: [],
    unreadCount: 0,
    unreadBySource: { legacy: 0, referral: 0 },
    loading: false,
    actionLoading: null,
    pollInterval: null,
    endpoint: process.env.VUE_APP_ROOT_API,
    defaultAvatar: require('@/assets/images/avatars/1.png'),
    perfectScrollbarOptions: {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    },
    icons: {
      mdiBellOutline,
    },
    notificationIcons: {
      connection_request: mdiAccountPlusOutline,
      connection_accepted: mdiAccountCheckOutline,
      affiliate_application: mdiAccountPlusOutline,
      affiliate_active: mdiCheckCircleOutline,
      affiliate_rejected: mdiCloseCircleOutline,
      affiliate_suspended: mdiCloseCircleOutline,
      affiliate_payout: mdiCheckCircleOutline,
      referral_reward: mdiCheckCircleOutline,
    },
    notificationColors: {
      connection_request: 'primary',
      connection_accepted: 'success',
      affiliate_active: 'success',
      affiliate_rejected: 'warning',
      affiliate_suspended: 'warning',
      affiliate_payout: 'success',
      referral_reward: 'success',
    },
  }),
  mounted() {
    this.fetchUnreadCount();
    // Poll for new notifications every 60 seconds
    this.pollInterval = setInterval(() => {
      this.fetchUnreadCount();
    }, 60000);
  },
  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },
  methods: {
    getAuthHeaders() {
      const token = this.$store.state.jwtToken;
      return token ? { Authorization: token } : {};
    },

    async fetchUnreadCount() {
      if (!this.$store.state.jwtToken) return;
      const results = await this.notificationSources(1);
      this.updateSourceCounts(results);
    },

    notificationSources(limit) {
      return Promise.allSettled([
        axios.get(`${this.endpoint}/getNotifications?limit=${limit}`, { headers: this.getAuthHeaders() }).then(res => res.data),
        referrals.notifications(limit),
      ]);
    },

    updateSourceCounts(results) {
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') this.unreadBySource[index ? 'referral' : 'legacy'] = result.value.unreadCount || 0;
      });
      this.updateUnreadCount();
    },

    updateUnreadCount() { this.unreadCount = this.unreadBySource.legacy + this.unreadBySource.referral; },

    async fetchNotifications() {
      this.loading = true;
      try {
        const results = await this.notificationSources(20);
        this.updateSourceCounts(results);
        this.notifications = results.flatMap((result, index) => {
          const source = index ? 'referral' : 'legacy';

          return result.status === 'fulfilled'
            ? (result.value.notifications || []).map(item => ({ ...item, _source: source }))
            : this.notifications.filter(item => item._source === source);
        }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 20);
      } catch {
        // silent
      }
      this.loading = false;
    },

    onMenuToggle(isOpen) {
      if (isOpen) {
        this.fetchNotifications();
      }
    },

    async onNotificationClick(notification) {
      // Mark as read
      if (!notification.is_read) {
        try {
          if (notification._source === 'referral') await referrals.markNotificationsRead(notification.id);
          else await axios.post(
            `${this.endpoint}/markNotificationsRead`,
            { notificationId: notification.id },
            { headers: this.getAuthHeaders() },
          );
          this.$set(notification, 'is_read', 1);
          this.unreadBySource[notification._source] = Math.max(0, this.unreadBySource[notification._source] - 1);
          this.updateUnreadCount();
        } catch {
          // silent
        }
      }
      const path = notification._source === 'referral' && notification.data && notification.data.path;
      if (['/referrals', '/admin/affiliates'].includes(path)) {
        this.isMenuOpen = false;
        if (this.$route.path !== path) await this.$router.push(path);
      }
    },

    isActionable(notification) {
      return notification.type === 'connection_request'
        && !notification._actionResult
        && notification.data
        && notification.data.connectionId
        && !notification.data.connectionStatus;
    },

    async handleConnectionAction(notification, action) {
      this.actionLoading = notification.id + '-' + action;
      const resultLabel = action === 'accept' ? 'accepted' : 'rejected';
      const idx = this.notifications.indexOf(notification);

      try {
        await axios.post(
          `${this.endpoint}/handleConnectionRequest`,
          { connectionId: notification.data.connectionId, action },
          { headers: this.getAuthHeaders() },
        );
      } catch {
        // Connection may have already been handled from Pending tab
      }

      // Always update UI — replace the object in the array for Vue 2 reactivity
      if (idx !== -1) {
        this.$set(this.notifications, idx, { ...notification, _actionResult: resultLabel, is_read: 1 });
      }

      // Mark notification as read
      if (!notification.is_read) {
        try {
          await axios.post(
            `${this.endpoint}/markNotificationsRead`,
            { notificationId: notification.id },
            { headers: this.getAuthHeaders() },
          );
          this.unreadBySource.legacy = Math.max(0, this.unreadBySource.legacy - 1);
          this.updateUnreadCount();
        } catch {
          // silent
        }
      }

      this.actionLoading = null;
    },

    async markAllRead() {
      const results = await Promise.allSettled([
        axios.post(`${this.endpoint}/markNotificationsRead`, {}, { headers: this.getAuthHeaders() }),
        referrals.markNotificationsRead(),
      ]);
      results.forEach((result, index) => {
        const source = index ? 'referral' : 'legacy';
        if (result.status !== 'fulfilled') return;
        this.notifications.filter(n => n._source === source).forEach(n => { this.$set(n, 'is_read', 1); });
        this.unreadBySource[source] = 0;
      });
      this.updateUnreadCount();
    },

    getNotificationAvatar(notification) {
      if (notification.data && notification.data.fromAvatar) {
        return `https://self-biography-system.s3.us-east-2.amazonaws.com/${notification.data.fromAvatar}`;
      }
      return null;
    },

    getNotificationIcon(type) {
      return this.notificationIcons[type] || mdiAccountOutline;
    },

    getNotificationColor(type) {
      return this.notificationColors[type] || 'primary';
    },

    formatTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
  },
}
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.app-bar-notification-content-container {
  .read-all-btn-list-item {
    padding-top: 14px;
    padding-bottom: 14px;
    min-height: unset;
  }
}

.ps-user-notifications {
  max-height: calc(var(--vh, 1vh) * 80);
}

.notification-menu-content {
  @media #{map-get($display-breakpoints, 'xs-only')} {
    min-width: calc(100vw - (1.5rem * 2)) !important;
    left: 50% !important;
    transform: translateX(-50%);
  }
}

.unread-notification {
  background-color: rgba(145, 85, 253, 0.04);
}

.notification-item .v-list-item__subtitle {
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
